import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from "react";

export const DEVELOPER_MODE_TAP_THRESHOLD = 10;

type DeveloperModeContextValue = {
    developerModeEnabled: boolean;
    registerAboutDeviceTap: () => void;
};

const DeveloperModeContext = createContext<DeveloperModeContextValue | null>(null);
DeveloperModeContext.displayName = "DeveloperMode";

export const useDeveloperMode = (): DeveloperModeContextValue => {
    const ctx = useContext(DeveloperModeContext);
    if (!ctx) {
        throw new Error("useDeveloperMode must be used within DeveloperModeProvider");
    }
    return ctx;
};

type Props = {
    children: ReactNode;
};

/**
 * In-memory developer mode (clears on full page reload).
 * Unlocked by tapping "About Device" in settings {@link DEVELOPER_MODE_TAP_THRESHOLD} times.
 */
const DeveloperModeProvider = ({ children }: Props) => {
    const [developerModeEnabled, setDeveloperModeEnabled] = useState(false);
    const [aboutDeviceTapCount, setAboutDeviceTapCount] = useState(0);

    const registerAboutDeviceTap = useCallback(() => {
        if (developerModeEnabled) return;

        setAboutDeviceTapCount((prev) => {
            const next = prev + 1;
            if (next >= DEVELOPER_MODE_TAP_THRESHOLD) {
                setDeveloperModeEnabled(true);
            }
            return next;
        });
    }, [developerModeEnabled]);

    const value = useMemo(
        () => ({ developerModeEnabled, registerAboutDeviceTap }),
        [developerModeEnabled, registerAboutDeviceTap]
    );

    return (
        <DeveloperModeContext.Provider value={value}>
            {children}
        </DeveloperModeContext.Provider>
    );
};

export default DeveloperModeProvider;
